import type { GatewayBrowserClient } from "../gateway.ts";
import type { AgentsWorkspaceListResult, AgentsWorkspaceReadResult } from "../types.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";

export type WorkspaceTreeNode = AgentsWorkspaceListResult["tree"][number];

type LegacyAgentsWorkspaceListResult = {
  agentId: string;
  workspace: string;
  truncated?: boolean;
  tree?: WorkspaceTreeNode[];
};

type LegacyAgentsWorkspaceReadResult = {
  agentId: string;
  workspace: string;
  path: string;
  size?: number;
  updatedAtMs?: number;
  content?: string;
  truncated?: boolean;
};

function isUnknownWorkspaceMethodError(err: unknown): boolean {
  const msg = String(err);
  return (
    msg.includes("unknown method: agents.workspace.list") ||
    msg.includes("unknown method: agents.workspace.read")
  );
}

export function isStaleWorkspaceSessionKeyError(err: unknown): boolean {
  const msg = String(err);
  return (
    msg.includes("unexpected property 'sessionKey'") ||
    msg.includes('unexpected property "sessionKey"')
  );
}

export function formatChatWorkspaceError(err: unknown): string {
  if (isUnknownWorkspaceMethodError(err)) {
    return "Workspace file browser needs a newer gateway. Rebuild (pnpm build) and restart the gateway service, then click Refresh.";
  }
  if (isStaleWorkspaceSessionKeyError(err)) {
    return "Gateway is missing session-scoped workspace support. Rebuild (pnpm build) and restart the gateway service, then click Refresh.";
  }
  return String(err);
}

function normalizeLegacyListResult(
  sessionKey: string,
  res: LegacyAgentsWorkspaceListResult,
): AgentsWorkspaceListResult {
  return {
    agentId: res.agentId,
    sessionKey,
    workspace: res.workspace,
    isolated: false,
    scope: "session",
    truncated: Boolean(res.truncated),
    tree: Array.isArray(res.tree) ? res.tree : [],
  };
}

export type ChatWorkspaceState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  agentsList?: { defaultId?: string } | null;
  agentsDefaultId?: string;
  /** When set, list/read uses fleet project root instead of session-output. */
  chatActiveProjectId?: string | null;
  chatWorkspaceLoading: boolean;
  chatWorkspaceError: string | null;
  chatWorkspaceAgentId: string | null;
  chatWorkspacePath: string | null;
  chatWorkspaceScope: "sandbox" | "session" | null;
  chatWorkspaceTruncated: boolean;
  chatWorkspaceTree: WorkspaceTreeNode[];
  chatWorkspaceSelectedPath: string | null;
  chatWorkspaceFileLoading: boolean;
  chatWorkspaceFileError: string | null;
  chatWorkspaceFileContent: string | null;
  chatWorkspaceFileTruncated: boolean;
};

async function requestWorkspaceList(
  client: GatewayBrowserClient,
  agentId: string,
  sessionKey: string,
  projectId?: string | null,
): Promise<AgentsWorkspaceListResult> {
  const params: Record<string, string> = { agentId, sessionKey };
  if (projectId?.trim()) {
    params.projectId = projectId.trim();
  }
  try {
    return await client.request<AgentsWorkspaceListResult>("agents.workspace.list", params);
  } catch (err) {
    if (!isStaleWorkspaceSessionKeyError(err)) {
      throw err;
    }
    const legacy = await client.request<LegacyAgentsWorkspaceListResult>("agents.workspace.list", {
      agentId,
    });
    return normalizeLegacyListResult(sessionKey, legacy);
  }
}

async function requestWorkspaceRead(
  client: GatewayBrowserClient,
  agentId: string,
  sessionKey: string,
  path: string,
  projectId?: string | null,
): Promise<AgentsWorkspaceReadResult> {
  const params: Record<string, string> = { agentId, sessionKey, path };
  if (projectId?.trim()) {
    params.projectId = projectId.trim();
  }
  try {
    return await client.request<AgentsWorkspaceReadResult>("agents.workspace.read", params);
  } catch (err) {
    if (!isStaleWorkspaceSessionKeyError(err)) {
      throw err;
    }
    const legacy = await client.request<LegacyAgentsWorkspaceReadResult>("agents.workspace.read", {
      agentId,
      path,
    });
    return {
      agentId: legacy.agentId,
      sessionKey,
      workspace: legacy.workspace,
      isolated: false,
      scope: "session",
      path: legacy.path,
      size: legacy.size ?? 0,
      updatedAtMs: legacy.updatedAtMs ?? 0,
      content: legacy.content ?? "",
      truncated: Boolean(legacy.truncated),
    };
  }
}

export async function loadChatWorkspace(state: ChatWorkspaceState) {
  if (!state.client || !state.connected) {
    state.chatWorkspaceTree = [];
    state.chatWorkspacePath = null;
    state.chatWorkspaceScope = null;
    state.chatWorkspaceError = null;
    return;
  }
  const defaultAgentId = state.agentsList?.defaultId ?? state.agentsDefaultId ?? "main";
  const agentId = resolveAgentIdForSession(state.sessionKey, defaultAgentId);
  if (state.chatWorkspaceLoading && state.chatWorkspaceAgentId === agentId) {
    return;
  }
  state.chatWorkspaceLoading = true;
  state.chatWorkspaceError = null;
  state.chatWorkspaceAgentId = agentId;
  try {
    const res = await requestWorkspaceList(
      state.client,
      agentId,
      state.sessionKey,
      state.chatActiveProjectId,
    );
    state.chatWorkspacePath = res?.workspace ?? null;
    state.chatWorkspaceScope = res?.scope ?? null;
    state.chatWorkspaceTruncated = Boolean(res?.truncated);
    state.chatWorkspaceTree = Array.isArray(res?.tree) ? res.tree : [];
  } catch (err) {
    state.chatWorkspaceError = formatChatWorkspaceError(err);
    state.chatWorkspaceTree = [];
    state.chatWorkspacePath = null;
    state.chatWorkspaceScope = null;
  } finally {
    state.chatWorkspaceLoading = false;
  }
}

export async function readChatWorkspaceFile(state: ChatWorkspaceState, relativePath: string) {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.chatWorkspaceAgentId ?? resolveAgentIdForSession(state.sessionKey, "main");
  const path = relativePath.trim();
  if (!path) {
    return;
  }
  state.chatWorkspaceSelectedPath = path;
  state.chatWorkspaceFileLoading = true;
  state.chatWorkspaceFileError = null;
  try {
    const res = await requestWorkspaceRead(
      state.client,
      agentId,
      state.sessionKey,
      path,
      state.chatActiveProjectId,
    );
    state.chatWorkspaceFileContent = res?.content ?? "";
    state.chatWorkspaceFileTruncated = Boolean(res?.truncated);
  } catch (err) {
    state.chatWorkspaceFileContent = null;
    state.chatWorkspaceFileError = formatChatWorkspaceError(err);
  } finally {
    state.chatWorkspaceFileLoading = false;
  }
}
